import { useState, useEffect, useCallback } from 'react'
import { persistor } from '../../redux/store'
import Api from '../../data'

import { onSectionClick as onSectionClickAnalytics } from './analytics'
import {
    updateTestDataProps,
    updatePropsOnLoad,
    attachRefreshEventListeners,
} from './helper'
import { ANTHE_EXAM_TYPES } from 'constants/examTypeConstants'

const WARNING_TRIGGER_COUNT = 5

function useTestPanelData(props) {
    const [errorMsg, setErrorMsg] = useState('')
    const [isError, setIsError] = useState(false)
    const [eventType, setEventType] = useState('')
    const [timer, setTimer] = useState()
    const [disconnectTime, setDisconnectTime] = useState(null)
    const [showGoBackPopUp, setShowGoBackPopUp] = useState(false)
    const [connection, setConnection] = useState('')
    const [showSpinner, setShowSpinner] = useState(false)
    const [redirectToResults, setRedirectToResults] = useState(false)
    const [tabSwitchCount, setTabSwitchCount] = useState(0)
    const [showWarningPopup, setShowWarningPopup] = useState(false)

    const isMock = JSON.parse(sessionStorage.getItem('is_mock'))
    const isPreview =
        sessionStorage.getItem('tp_preview_token') &&
        sessionStorage.getItem('test_id')
            ? true
            : false

    const mock_test_submit = props.mock_test_submit

    const saveTotalData = async (activityType) => {
        if (ANTHE_EXAM_TYPES.includes(props.queryparam.exam_type.toLowerCase())) {
            setRedirectToResults(false)
        }
        window.location.href = props.base_url
    }

    useEffect(() => {
        const testpanelElem = document.getElementById('testpanel')

        if (testpanelElem) testpanelElem.addEventListener('logout', handleLogout)

        return () =>
            testpanelElem &&
            testpanelElem.removeEventListener('logout', handleLogout)
    }, [document.getElementById('testpanel')])

    useEffect(() => {
        if (
            props.testData?.questionset?.length > 0 &&
            props.testDataConfirmation.isTestDataLoaded &&
            !isError &&
            !mock_test_submit
        ) {
            clearTimeout(timer)
            const newTimer = setTimeout(countDown, 1000)
            setTimer(newTimer)
        } else if (mock_test_submit) {
            props.hideTimer(true)
        }

        return () => {
            clearTimeout(timer)
        }
    }, [disconnectTime, props.testDataConfirmation?.isTestDataLoaded, isError])

    useEffect(() => {
        if (navigator.onLine) {
            if (localStorage.getItem('offline') === 'true') {
                localStorage.setItem('offline', 'false')
                setConnection('online')
            }
        } else {
            localStorage.setItem('offline', 'true')
            setConnection('offline')
        }
    }, [navigator.onLine])

    async function countDown() {
        if (disconnectTime <= 0) {
            setShowSpinner(true)
            setDisconnectTime(0)
            props.hideTimer(true)
            await Api.logActivityType(props, 'Auto_Logout')
            await saveTotalData('Auto logged out due to exceeding the idle time')
            window.location.href = props.base_url
            persistor.pause()
            persistor.flush().then(() => {
                return persistor.purge()
            })
            clearTimeout(timer)
        } else {
            setDisconnectTime((time) => time - 1)
        }
    }

    const handleSubmit = useCallback(
        async (props) => {
            await saveTotalData('Leave Test')
            persistor.pause()
            persistor.flush().then(() => {
                return persistor.purge()
            })
        },
        [props.queryparam, props.base_url, saveTotalData],
    )

    function handleSectionClick(index, data) {
        const _index = parseInt(index, 0)
        if (_index !== props.active) {
            props.setActive(_index)
            props.setActiveSection(_index)
        }

        if (isPreview || isMock) {
            return
        }

        onSectionClickAnalytics(props, _index)
    }

    function handleExitScreen() {
        handleVisibilityChange('fullscreen-exit')
    }

    function handleTabSwitch() {
        document.visibilityState !== 'visible' &&
            handleVisibilityChange('tab-change')
    }

    function handleVisibilityChange(activityType) {
        const userTestId = props.testStatus?.testStatus?.data?.userTestId
        const testId = props.instructions?.testData?.id
        const testAttemptNo = props.queryparam?.test_attempt
        const rollNo = props.testStatus?.testStatus?.data?.testRollNo
        const questionId = props.current_question?.question?.questionid
        const deviceId = props.deviceId

        if (userTestId && rollNo && questionId) {
            Api.postTabSwitchFullScreenExitLogs({
                userTestId,
                testId,
                testAttemptNo,
                rollNo,
                questionId,
                deviceId,
                activityType,
            })
                .then((res) => res.data)
                .then(() => {
                    if (activityType === 'tab-change') {
                        setTabSwitchCount((count) => {
                            const updatedCount = count + 1
                            if (
                                updatedCount % WARNING_TRIGGER_COUNT === 0 &&
                                updatedCount !== 0
                            ) {
                                setShowWarningPopup(true)
                            }
                            return updatedCount
                        })
                    }
                })
                .catch((err) => console.error(err))
        }
    }

    function getTabSwitchCount() {
        const userTestId = props.testStatus?.testStatus?.data?.userTestId
        const testAttemptNo = props.queryparam?.test_attempt
        const deviceId = props.deviceId

        if (userTestId) {
            Api.getTabSwitchCount({ userTestId, testAttemptNo, deviceId })
                .then((res) => res.data)
                .then((res) => {
                    setTabSwitchCount(res?.tabChangeCount)
                })
                .catch(handleError)
        }
    }

    function handlePreviewData() {
        if (Object.keys(props?.testPreviewData.data).length) {
            updateTestDataProps(props, props?.testPreviewData.data, [])
        } else {
            fetchTestData('', sessionStorage.getItem('test_id'))
        }
        props.hideTimer(false)
    }

    function fetchTestState(res) {
        Api.getTestState(
            props.testStatus?.testStatus?.data?.userTestId.replace(/"/g, ''),
        )
            .then((res) => res.data)
            .then((res1) => {
                updateTestDataProps(props, res, res1)
                props.setTestDataLoadedStatus(true)
            })
            .catch(handleError)
    }

    function handleError(error) {
        setShowSpinner(false)
        if (error.response && error.response.status >= 400) {
            setErrorMsg(
                error.response.data.detailMessage ||
                    error.response.message ||
                    error.response.data.detailMessage,
            )
            setIsError(true)
        } else {
            setErrorMsg(error?.message)
            setIsError(true)
        }
    }

    function fetchTestData(testKey, testId) {
        const isSubmitted = JSON.parse(sessionStorage.getItem('isSubmitted'))
        Api.getTestQuestionAnswerData(testKey, testId)
            .then((res) => {
                return testKey && isMock ? res.data.data : res.data
            })
            .then((res) => {
                if (testKey && (props.isExistingTest || isSubmitted)) {
                    fetchTestState(res)
                } else {
                    updateTestDataProps(props, res, [])
                    props.setTestDataLoadedStatus(true)
                }
                setShowSpinner(false)
            })
            .catch(handleError)
    }

    function handleTestData() {
        try {
            updatePropsOnLoad(
                props,
                setRedirectToResults,
                setShowGoBackPopUp,
                setConnection,
                setDisconnectTime,
            )
            attachRefreshEventListeners(setShowGoBackPopUp)

            const questionSet = props.testData?.questionset
            const isSubmitted = JSON.parse(sessionStorage.getItem('isSubmitted'))
            isSubmitted && setShowSpinner(true)

            if (Array.isArray(questionSet) && questionSet.length && !isSubmitted) {
                props.onQuestionLoad(questionSet)
                props.setTestDataLoadedStatus(true)
                questionSet.forEach((data, index) => {
                    if (props.active === index) {
                        props.current_question['subject'] = data.subject
                        props.UpdateCurrentQuestion(props.current_question)
                    }
                })
            } else {
                const testId = props.instructions?.testData?.id
                const testKey =
                    props.instructions?.testData?.testKey ||
                    (testId ? 'Test_' + testId + '.json' : null)

                if (testKey) {
                    fetchTestData(testKey, testId)
                } else {
                    setShowGoBackPopUp(false)
                    setShowSpinner(false)
                    setIsError(true)
                    setErrorMsg(
                        'Test data is not available. Please login again and restart the test!',
                    )
                    setTimeout(() => {
                        window.location.href = Api.getDashboardURL()
                    }, 3000)
                }
            }
        } catch (error) {
            handleError(error)
        }
    }

    function handleLogout(e) {
        if (e === 'true') {
            setErrorMsg(
                'Your test has been reset. Please login again to start your test.',
            )
            setIsError(true)
            setTimeout(() => {
                window.location.href = Api.getDashboardURL()
            }, 5000)
        } else {
            setErrorMsg(e.detail.message)
            setIsError(true)
            setEventType(e?.type)
            setTimeout(() => {
                window.location.href = props.base_url
            }, 3000)
        }
        persistor.pause()
        persistor.flush().then(() => {
            return persistor.purge()
        })
    }

    return {
        isError,
        eventType,
        showGoBackPopUp,
        showSpinner,
        connection,
        redirectToResults,
        errorMsg,
        tabSwitchCount,
        showWarningPopup,
        setShowGoBackPopUp,
        getTabSwitchCount,
        setShowWarningPopup,
        setDisconnectTime,
        handleTabSwitch,
        handleExitScreen,
        handlePreviewData,
        handleSectionClick,
        handleTestData,
        handleLogout,
        handleSubmit,
    }
}

export default useTestPanelData
